"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createClient = void 0;
const sdk_1 = require("@prezly/sdk");
/**
 * Sort order to list stories chronologically, with pinned stories on top.
 */
const CHRONOLOGICALLY = sdk_1.SortOrder.combine(sdk_1.SortOrder.desc('is_pinned'), sdk_1.SortOrder.desc('published_at'));
function createClient(prezly, newsroomUuid, newsroomThemeUuid, { formats = [sdk_1.Story.FormatVersion.SLATEJS_V4], cache = false } = {}) {
    const client = {
        newsroom() {
            return prezly.newsrooms.get(newsroomUuid);
        },
        theme() {
            return newsroomThemeUuid
                ? prezly.newsroomThemes.get(newsroomUuid, newsroomThemeUuid)
                : undefined;
        },
        async themeSettings() {
            const theme = await client.theme();
            return theme?.settings;
        },
        languages() {
            return prezly.newsroomLanguages.list(newsroomUuid).then((data) => data.languages);
        },
        async usedLanguages() {
            const languages = await client.languages();
            return languages.filter((lang) => lang.public_stories_count > 0);
        },
        async locales() {
            const languages = await client.languages();
            return languages.map((lang) => lang.code);
        },
        async defaultLanguage() {
            const languages = await client.languages();
            const defaultLanguage = languages.find((lang) => lang.is_default);
            if (!defaultLanguage) {
                throw new Error('A newsroom is expected to always have a default language. Something is wrong.');
            }
            return defaultLanguage;
        },
        async companyInformation(code) {
            const languageSettings = code
                ? await client.languageOrDefault(code)
                : await client.defaultLanguage();
            return languageSettings.company_information;
        },
        async notifications(locale) {
            const languageSettings = await client.languageOrDefault(locale);
            return languageSettings.notifications;
        },
        async defaultLocale() {
            const defaultLanguage = await client.defaultLanguage();
            return defaultLanguage.code;
        },
        async language(code) {
            const languages = await client.languages();
            return languages.find((lang) => (!code && lang.is_default) || lang.locale.code === code);
        },
        async languageOrDefault(code) {
            return (await client.language(code)) ?? (await client.defaultLanguage());
        },
        categories() {
            return prezly.newsroomCategories.list(newsroomUuid, { sortOrder: '+order' });
        },
        async category(arg) {
            const categories = await client.categories();
            if (typeof arg === 'number') {
                return categories.find((category) => category.id === arg);
            }
            return categories.find((category) => sdk_1.Category.translations(category).some((translation) => translation.slug === arg));
        },
        async translatedCategories(locale, categories) {
            return sdk_1.Category.translations(categories ?? (await client.categories()), locale);
        },
        async translatedCategory(locale, slug) {
            const translations = await client.translatedCategories(locale);
            return translations.find((category) => category.slug === slug);
        },
        featuredContacts() {
            return prezly.newsroomContacts.search(newsroomUuid, {
                query: {
                    is_featured: true,
                },
            });
        },
        galleries(params = {}) {
            const { offset, limit, type } = params;
            return prezly.newsroomGalleries.search(newsroomUuid, {
                limit,
                offset,
                scope: {
                    status: sdk_1.NewsroomGallery.Status.PUBLIC,
                    is_empty: false,
                    type,
                },
            });
        },
        async gallery(uuid) {
            try {
                return await prezly.newsroomGalleries.get(newsroomUuid, uuid);
            }
            catch (error) {
                if (error instanceof sdk_1.ApiError && isNotAvailableError(error)) {
                    return null;
                }
                throw error;
            }
        },
        stories(params, options = {}) {
            const { search, query, offset = 0, limit, category, locale, highlighted = 0 } = params;
            const { include = [] } = options;
            const localeCode = locale && typeof locale === 'object' ? locale.code : locale;
            return prezly.stories.search({
                sortOrder: CHRONOLOGICALLY,
                formats,
                limit: offset === 0 ? limit + highlighted : limit,
                offset: offset > 0 ? offset + highlighted : offset,
                search,
                query: mergeQueries(query, {
                    [`category.id`]: category ? { $any: [category.id] } : undefined,
                    [`newsroom.uuid`]: { $in: [newsroomUuid] },
                    [`locale`]: localeCode ? { $in: [localeCode] } : undefined,
                    [`status`]: { $in: [sdk_1.Story.Status.PUBLISHED] },
                    [`visibility`]: { $in: [sdk_1.Story.Visibility.PUBLIC] },
                }),
                include,
            });
        },
        async allStories(params = {}, options = {}) {
            const { include = [] } = options;
            const newsroom = await client.newsroom();
            const chunkSize = 200;
            // Note: This is a counter of ALL stories, public, private, or drafts.
            //       Depending on the specific newsroom data, the rate of overfetching may be significant.
            const maxStories = newsroom.stories_number;
            const pages = Math.ceil(maxStories / chunkSize);
            const promises = Array.from({ length: pages }, (_, chunkIndex) => client.stories({
                ...params,
                limit: chunkSize,
                offset: chunkIndex * chunkSize,
            }, { include }));
            return (await Promise.all(promises)).flatMap((response) => response.stories);
        },
        async story(params, options = {}) {
            const { include = [] } = options;
            if (params.uuid) {
                try {
                    return await prezly.stories.get(params.uuid, {
                        formats,
                        include: [...sdk_1.Stories.EXTENDED_STORY_INCLUDED_EXTRA_FIELDS, ...include],
                    });
                }
                catch (error) {
                    if (error instanceof sdk_1.ApiError && isNotAvailableError(error)) {
                        return null;
                    }
                    throw error;
                }
            }
            try {
                return await prezly.stories.getBySlug(params.slug, {
                    formats,
                    query: {
                        [`newsroom.uuid`]: { $in: [newsroomUuid] },
                        [`status`]: {
                            $in: [sdk_1.Story.Status.PUBLISHED, sdk_1.Story.Status.EMBARGO],
                        },
                        [`visibility`]: {
                            $in: [
                                sdk_1.Story.Visibility.PUBLIC,
                                sdk_1.Story.Visibility.PRIVATE,
                                sdk_1.Story.Visibility.EMBARGO,
                            ],
                        },
                    },
                    include: [...sdk_1.Stories.EXTENDED_STORY_INCLUDED_EXTRA_FIELDS, ...include],
                });
            }
            catch (error) {
                if (error instanceof sdk_1.ApiError && isNotAvailableError(error)) {
                    return null;
                }
                throw error;
            }
        },
    };
    if (cache) {
        injectCache(client);
    }
    return client;
}
exports.createClient = createClient;
function injectCache(client) {
    const cachedCalls = new Map();
    const methodNames = Object.keys(client);
    methodNames.forEach((methodName) => {
        const uncachedFn = client[methodName].bind(client);
        // eslint-disable-next-line no-param-reassign
        client[methodName] = (...args) => {
            const key = `${methodName}:${JSON.stringify(args)}`;
            const cached = cachedCalls.get(key);
            if (cached)
                return cached;
            const value = uncachedFn(...args);
            cachedCalls.set(key, value);
            return value;
        };
    });
}
function mergeQueries(...queries) {
    const queryObjects = queries
        .filter((query) => Boolean(query))
        .map((query) => {
        if (typeof query === 'string') {
            return JSON.parse(query);
        }
        return query;
    })
        .filter((query) => Boolean(query))
        .filter((query) => Object.keys(query).length > 0);
    if (queryObjects.length === 0) {
        return undefined;
    }
    if (queryObjects.length === 1) {
        return queryObjects[0];
    }
    return { $and: queryObjects };
}
const ERROR_CODE_NOT_FOUND = 404;
const ERROR_CODE_FORBIDDEN = 403;
const ERROR_CODE_GONE = 410;
function isNotAvailableError(error) {
    return (error.status === ERROR_CODE_NOT_FOUND ||
        error.status === ERROR_CODE_GONE ||
        error.status === ERROR_CODE_FORBIDDEN);
}
//# sourceMappingURL=ContentDelivery.js.map